/**
 * How well a dictation model hears the languages the user speaks, as one
 * line under its name in Settings › Voice and onboarding's voice step. The
 * numbers are the catalogue's (`DICTATION_<MODEL>_ACCURACY` in
 * dictation-models.env, from docs/specs/2026-10-31-dictation-accuracy-by-
 * language.json): 100% minus the measured error rate.
 */
import { translate } from "@/i18n";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";
import { languageDisplayName } from "@/lib/language-display-name";

import type { DictationModelStatus } from "./dictation-models";

/** Below this a model gets too many words wrong to dictate with. */
const USABLE_PERCENT = 50;

/** The most languages one line names. */
const SHOWN_LANGUAGES = 3;

type Rated = Pick<DictationModelStatus, "languages" | "accuracy">;

/** Whether `model` listens for `language`, a dictation language code. */
export function servesLanguage(model: Rated, language: string): boolean {
  return model.languages === null || model.languages.includes(language);
}

/**
 * The first three of `languages` the model serves, each with its accuracy -
 * or that the model is not usable for it - in the user's order. A language
 * with no number says nothing; with none at all, the line says so.
 */
export function accuracyLine(
  model: Rated,
  languages: readonly string[],
): string {
  const parts = languages
    .filter((language) => servesLanguage(model, language))
    .slice(0, SHOWN_LANGUAGES)
    .flatMap((language) => {
      const percent = model.accuracy[language];
      if (percent === undefined) return [];
      const name = languageDisplayName(language);
      return [
        percent < USABLE_PERCENT
          ? translate(settingsVoiceMessages, "modelNotUsable", {
              language: name,
            })
          : translate(settingsVoiceMessages, "modelAccuracy", {
              language: name,
              percent,
            }),
      ];
    });
  return parts.length > 0
    ? parts.join(" · ")
    : translate(settingsVoiceMessages, "modelAccuracyNotMeasured");
}
